// src/pages/products/hsquare.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle2, BarChart3, Database, ShieldCheck, Users, Calendar, IndianRupee, Link } from 'lucide-react';

const Hsquare = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 1000], [0, -120]); // strong parallax
  const badgeY = useTransform(scrollY, [0, 600], [0, -50]);

  const handleFreeConsultation = () => navigate('/contact');

  const keyCapabilities = [
    { icon: Database, title: "Multi-code simultaneous processing", desc: "Handle compliance for hundreds of locations concurrently" },
    { icon: BarChart3, title: "Real-time interactive dashboards", desc: "Customisable views by state, entity, or compliance type" },
    { icon: Calendar, title: "Customised report generation", desc: "Auto-scheduled reports for management and audit teams" },
    { icon: CheckCircle2, title: "Data validation engine", desc: "Flags anomalies, duplicates, and missing data before submissions" },
    { icon: Users, title: "Live progress tracking", desc: "See filing status, approvals, and pending items in real time" },
    { icon: ShieldCheck, title: "Enterprise-grade AES-256 encryption", desc: "Complete data security across the platform" },
    { icon: IndianRupee, title: "Flexible pay-per-use pricing", desc: "Scalable to business size and compliance volume" },
    { icon: Users, title: "Role-based access control", desc: "For compliance teams, HR, finance, and management" },
    { icon: Link, title: "Integration APIs", desc: "Seamless connection with HRMS and payroll systems" }
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">
      {/* Hero – ultra-premium with enhanced animations */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50 pt-24 overflow-hidden">
        {/* Multiple animated orbs for depth */}
        <motion.div
          animate={{ scale: [1, 1.22, 1], rotate: [0, 8, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-80 -left-80 w-[1200px] h-[1200px] bg-emerald-200/25 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.28, 1], rotate: [0, -10, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-80 -right-80 w-[1300px] h-[1300px] bg-cyan-200/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [-30, 30, -30], opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 14, repeat: Infinity }}
          className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-300/15 rounded-full blur-2xl"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              style={{ y: badgeY }}
              className="inline-flex items-center gap-5 bg-white/90 backdrop-blur-2xl border border-emerald-100/60 px-9 py-5 rounded-full mb-12 shadow-2xl"
            >
              <Database className="text-[#d97925]" size={32} />
              <span className="text-[#d97925] font-bold tracking-[0.25em] uppercase text-base">
                H-Square
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.2 }}
              className="text-6xl md:text-[110px] leading-[0.92] font-black tracking-[-0.05em] mb-12 bg-gradient-to-r from-[#d97925] via-[#d97925] to-[#d97925] bg-clip-text text-transparent"
            >
              H-Square
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 70 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.4 }}
              className="text-3xl md:text-4xl font-semibold text-zinc-800 max-w-4xl leading-tight"
            >
              Enterprise-Grade Compliance Intelligence at Scale
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.6 }}
              className="text-2xl md:text-3xl text-zinc-600 mt-8 max-w-4xl leading-tight"
            >
              Enterprise-grade compliance data processing engine for organisations managing large volumes of data across multiple locations. Real-time dashboards with simultaneous processing and proactive validation.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-32 md:py-40 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 xl:gap-24">
            {/* Left – Text & Lists */}
            <div className="lg:col-span-7 space-y-24 lg:space-y-32">
              {/* Who It's For */}
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1 }}
              >
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-10 bg-gradient-to-r from-[#d97925] to-[#d97925] bg-clip-text text-transparent">
                  Who It's For
                </h2>
                <p className="text-xl md:text-2xl text-zinc-700 leading-relaxed mb-8">
                  Large enterprises, multi-unit businesses, compliance teams managing 50+ establishments, and organisations seeking a single source of truth for compliance status.
                </p>
              </motion.div>

              {/* Key Capabilities */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-r from-[#d97925] to-[#d97925] bg-clip-text text-transparent">
                  H-Square Key Capabilities
                </h3>

                <div className="grid md:grid-cols-2 gap-8">
                  {keyCapabilities.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 60, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07, type: "spring", stiffness: 90 }}
                      whileHover={{ y: -12, scale: 1.03, boxShadow: "0 20px 40px -10px rgba(16,185,129,0.25)" }}
                      className="bg-white border border-emerald-100/60 rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-[#d97925] transition-all duration-400 group"
                    >
                      <div className="flex items-start gap-5">
                        <div className="mt-1 w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                          <item.icon className="text-[#d97925]" size={26} />
                        </div>
                        <div>
                          <h4 className="text-xl font-semibold text-[#d97925] group-hover:text-emerald-700 transition-colors">
                            {item.title}
                          </h4>
                          <p className="text-[17px] leading-relaxed text-zinc-700 mt-2">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Business Outcomes */}
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-r from-[#d97925] to-[#d97925] bg-clip-text text-transparent">
                  Business Outcomes
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {[
                    "Reduce compliance team workload by up to 60% through automation",
                    "Eliminate manual errors in data processing and return filing",
                    "Gain complete real-time visibility into compliance status across all locations",
                    "Produce audit-ready reports in minutes, not days"
                  ].map((outcome, i) => (
                    <div
                      key={i}
                      className="bg-emerald-50/40 p-8 rounded-3xl border border-emerald-100 flex items-start gap-5 hover:bg-emerald-50 transition-colors"
                    >
                      <CheckCircle2 className="text-[#d97925] mt-1.5 flex-shrink-0" size={28} />
                      <p className="text-xl leading-relaxed font-medium">{outcome}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Pricing Model */}
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.6 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-8 bg-gradient-to-r from-[#d97925] to-[#d97925] bg-clip-text text-transparent">
                  Pricing Model
                </h3>
                <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-12 border border-emerald-100 shadow-xl">
                  <p className="text-xl md:text-2xl leading-relaxed text-zinc-800">
                    Flexible pay-per-use pricing. Contact the TickBox team for a customised quote based on entity count and compliance scope.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right – Visuals with parallax */}
            <motion.div
              style={{ y: imageY }}
              className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-12"
            >
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-zinc-200/80">
                <img
                  src=".././Assets/Images/Hsquare.jpg"
                  alt="H-Square Compliance Dashboard"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-10 left-10 text-white">
                  <p className="text-2xl font-bold drop-shadow-lg">Enterprise-grade compliance intelligence</p>
                  <p className="text-base opacity-90 mt-2 drop-shadow">Powered by H-Square</p>
                </div>
              </div>

              {/* Real-time Dashboard Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-10 border border-emerald-100 shadow-2xl"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-md">
                    <BarChart3 className="text-[#d97925]" size={40} />
                  </div>
                  <h4 className="text-3xl font-bold text-[#d97925]">Real-time Dashboards</h4>
                </div>
                <p className="text-lg text-zinc-800 leading-relaxed">
                  Customisable, interactive views — compliance status by state, entity, or type — updated live.
                </p>
              </motion.div>

              {/* Data Security Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-3xl p-10 border border-emerald-100 shadow-xl flex items-start gap-6"
              >
                <ShieldCheck className="text-[#d97925] mt-1" size={48} />
                <div>
                  <h4 className="text-3xl font-bold mb-4 text-[#d97925]">Enterprise-Grade Data Security</h4>
                  <p className="text-lg text-zinc-700">
                    Enterprise-grade security with TLS 1.3 and strong AES-based encryption ensures your compliance data is securely protected in transit.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1 }}
            className="mt-32 md:mt-40 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-3xl py-24 px-8 md:px-16 text-center shadow-2xl relative overflow-hidden"
          >
            {/* Subtle animated shine */}
            <motion.div
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-40"
            />

            <h3 className="text-5xl md:text-6xl font-bold tracking-tight mb-8 relative z-10">
              Ready to experience H-Square?
            </h3>
            <p className="text-2xl md:text-3xl mb-12 max-w-4xl mx-auto opacity-90 relative z-10">
              Enterprise-grade compliance intelligence — multi-location processing, real-time dashboards, and zero manual errors.
            </p>

            <motion.button
              whileHover={{ scale: 1.06, boxShadow: "0 30px 60px -15px rgba(16, 185, 129, 0.5)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFreeConsultation}
              className="inline-flex items-center gap-5 px-16 py-8 bg-white text-[#d97925] font-bold text-2xl md:text-3xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-all relative z-10"
            >
              Request H-Square Demo
              <ArrowRight size={36} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Hsquare;